import React, { useEffect, useState, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  TableHead,
  TableCell,
  TableRow,
  TableBody,
  Table,
  TableContainer,
  Paper,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
} from "@material-ui/core";
import { useSelector } from "react-redux";
import moment from "moment/moment";
import { formatearFecha } from "src/utils/formatTime";
import { pagosApi } from "src/services/pagosApi";
import { accesoClientesApi } from "src/services/accesoClientesApi";
import { IconButton, Tooltip } from "@material-ui/core";
import { Delete as DeleteIcon, Edit as EditIcon } from "@material-ui/icons";
import ModalEliminarPago from "./ModalEliminarPago";
import { useSnackbar } from "src/components/snackbar";
import PropTypes from "prop-types";
import { formatAsCLP } from "src/utils/formatNumber";
import ModalEditarPago from "./ModalEditarPago";

moment.locale("es");

const ModalVerFactura = ({ open, setOpen, factura, onRefetch }) => {
  const [detalleFactura, setDetalleFactura] = useState([]);
  const [mostrarDatosCheque, setMostrarDatosCheque] = useState(false);
  const [modalEliminarOpen, setModalEliminarOpen] = useState(false);
  const [pagoSeleccionado, setPagoSeleccionado] = useState(null);
  const [modalEditarOpen, setModalEditarOpen] = useState(false);
  const [pagoAEditar, setPagoAEditar] = useState(null);

  const { rol: rolObtenido } = useSelector((state) => state.auth.user);

  // RTK Query hooks
  const [triggerFacturaPlanes, resultFacturaPlanes] =
    pagosApi.useLazyObtenerDetalleFacturaPlanesQuery();
  const [triggerFacturaTareas, resultFacturaTareas] =
    pagosApi.useLazyObtenerDetalleFacturaTareasQuery();
  const [triggerFacturaAfecta, resultFacturaAfecta] =
    pagosApi.useLazyObtenerDetalleFacturaAfectaQuery();
  const [triggerFacturaClientePlanes, resultFacturaClientePlanes] =
    accesoClientesApi.useLazyObtenerDetalleFacturaClientePlanesQuery();
  const [eliminarPagoPlan, { isLoading: isLoadingPlan }] =
    pagosApi.useEliminarPagoPlanMutation();
  const [eliminarPagoTarea, { isLoading: isLoadingTarea }] =
    pagosApi.useEliminarPagoTareaMutation();
  const [editarPagoPlan, { isLoading: isLoadingEditPlan }] =
    pagosApi.useEditarPagoPlanMutation();
  const [editarPagoTarea, { isLoading: isLoadingEditTarea }] =
    pagosApi.useEditarPagoTareaMutation();
  const { enqueueSnackbar } = useSnackbar();

  // Determinar qué resultado usar basado en el tipo de factura y rol
  const currentResult = useMemo(() => {
    if (rolObtenido === "CLIENTE") {
      return resultFacturaClientePlanes;
    }

    switch (factura?.tipo) {
      case "PLAN":
        return resultFacturaPlanes;
      case "TAREA":
        return resultFacturaTareas;
      case "AFECTA":
        return resultFacturaAfecta;
      default:
        return { data: null, isLoading: false, error: null };
    }
  }, [
    rolObtenido,
    factura?.tipo,
    resultFacturaPlanes,
    resultFacturaTareas,
    resultFacturaAfecta,
    resultFacturaClientePlanes,
  ]);

  const { data, isLoading, error } = currentResult;

  // Función para abrir modal de eliminación
  const handleAbrirModalEliminar = (pago) => {
    setPagoSeleccionado(pago);
    setModalEliminarOpen(true);
  };

  // Función para cerrar modal de eliminación
  const handleCerrarModalEliminar = () => {
    setModalEliminarOpen(false);
    setPagoSeleccionado(null);
  };

  // Función para abrir modal de edición
  const handleAbrirModalEditar = (pago) => {
    setPagoAEditar(pago);
    setModalEditarOpen(true);
  };

  // Función para cerrar modal de edición
  const handleCerrarModalEditar = () => {
    setModalEditarOpen(false);
    setPagoAEditar(null);
  };

  // Función para editar pago
  const handleEditarPago = async ({ id, motivo, ...cambios }) => {
    try {
      let resultado;

      if (factura?.tipo === "PLAN") {
        resultado = await editarPagoPlan({
          id,
          motivo,
          rolUsuario: rolObtenido,
          ...cambios,
        }).unwrap();
      } else if (factura?.tipo === "TAREA") {
        resultado = await editarPagoTarea({
          id,
          motivo,
          rolUsuario: rolObtenido,
          ...cambios,
        }).unwrap();
      }

      // Actualizar estado local optimísticamente
      setDetalleFactura((prev) =>
        prev.map((pago) => (pago.id === id ? { ...pago, ...cambios } : pago)),
      );

      handleCerrarModalEditar();

      await onRefetch?.();

      // Mostrar mensaje de éxito
      enqueueSnackbar(resultado.message || "Pago editado correctamente", {
        variant: "success",
      });
    } catch (error) {
      console.error("Error editando pago:", error);
      enqueueSnackbar(error?.data?.message || "Error al editar el pago", {
        variant: "error",
      });
    }
  };

  const handleEliminarPago = async ({ id, motivo, enviarCorreo }) => {
    try {
      let resultado;

      if (factura?.tipo === "PLAN") {
        resultado = await eliminarPagoPlan({
          id,
          motivo,
          enviarCorreo,
          rolUsuario: rolObtenido,
        }).unwrap();
      } else if (factura?.tipo === "TAREA") {
        resultado = await eliminarPagoTarea({
          id,
          motivo,
          enviarCorreo,
          rolUsuario: rolObtenido,
        }).unwrap();
      }

      // Actualizar estado local optimísticamente
      setDetalleFactura((prev) => prev.filter((pago) => pago.id !== id));

      // Recalcular si hay cheques después de la eliminación
      const nuevosDetalles = detalleFactura.filter((pago) => pago.id !== id);
      const existeCheque = nuevosDetalles.find(
        (pago) => pago.metodoPago === "CHEQUE",
      );
      setMostrarDatosCheque(!!existeCheque);

      handleCerrarModalEliminar();

      await onRefetch?.();

      // Mostrar mensaje de éxito
      enqueueSnackbar(resultado.message || "Pago eliminado correctamente", {
        variant: "success",
      });
    } catch (error) {
      console.error("Error eliminando pago:", error);
      enqueueSnackbar(error?.data?.message || "Error al eliminar el pago", {
        variant: "error",
      });
    }
  };

  // Función para verificar si se puede modificar un pago (30 días)
  const puedeModificarPago = (fechaPago) => {
    const fecha = moment(fechaPago, ["YYYY/MM/DD", "DD/MM/YYYY"]);
    const diasTranscurridos = moment().diff(fecha, "days");
    return diasTranscurridos <= 30;
  };

  // Función para verificar permisos
  const tienePermisosEliminar = () => {
    return ["ADMIN", "SUPER_ADMIN"].includes(rolObtenido);
  };

  // Función para obtener datos según tipo de factura y rol
  const fetchDetalleFactura = () => {
    if (!factura) return;

    if (rolObtenido === "CLIENTE") {
      triggerFacturaClientePlanes(factura.folio);
    } else {
      switch (factura.tipo) {
        case "PLAN":
          triggerFacturaPlanes(factura.folio);
          break;
        case "TAREA":
          triggerFacturaTareas(factura.id);
          break;
        case "AFECTA":
          triggerFacturaAfecta(factura.folio);
          break;
        default:
          console.warn(`Tipo de factura no reconocido: ${factura.tipo}`);
      }
    }
  };

  // Actualizar estado cuando cambian los datos
  useEffect(() => {
    if (data && Array.isArray(data)) {
      if (data === -2) {
        window.location.reload();
        return;
      }

      setDetalleFactura(data);
      const existeCheque = data.find((pago) => pago.metodoPago === "CHEQUE");
      setMostrarDatosCheque(!!existeCheque);
    }
  }, [data]);

  // Cargar datos cuando se abre el modal
  useEffect(() => {
    if (open && factura) {
      fetchDetalleFactura();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, factura?.folio, factura?.id, factura?.tipo, rolObtenido]);

  const handleClose = () => {
    setOpen(false);
    // Limpiar estado al cerrar
    setDetalleFactura([]);
    setMostrarDatosCheque(false);
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div
          style={{ display: "flex", justifyContent: "center", padding: "20px" }}
        >
          <CircularProgress />
        </div>
      );
    }

    if (error) {
      return (
        <Alert severity="error" style={{ margin: "16px 0" }}>
          Error al cargar el detalle de la factura. Por favor, intenta
          nuevamente.
        </Alert>
      );
    }

    if (!detalleFactura || detalleFactura.length === 0) {
      return (
        <Alert severity="info" style={{ margin: "16px 0" }}>
          No se encontraron pagos para esta factura.
        </Alert>
      );
    }

    return (
      <TableContainer component={Paper}>
        <Table aria-label="detalle factura table">
          <TableHead>
            <TableRow>
              <TableCell>Fecha de Registro</TableCell>
              <TableCell>Fecha de Pago</TableCell>
              <TableCell>Valor</TableCell>
              <TableCell>Método Pago</TableCell>
              {mostrarDatosCheque && (
                <>
                  <TableCell>Banco</TableCell>
                  <TableCell>Número Cheque</TableCell>
                </>
              )}
              {rolObtenido !== "CLIENTE" && (
                <TableCell>Empleado Recibe Pago</TableCell>
              )}
              {/* Columna para Editar */}
              {tienePermisosEliminar() && rolObtenido !== "CLIENTE" && (
                <TableCell align="center">Editar</TableCell>
              )}
              {/* Columna de Acciones (Eliminar) */}
              {tienePermisosEliminar() && rolObtenido !== "CLIENTE" && (
                <TableCell align="center">Acciones</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {detalleFactura.map((row, index) => (
              <TableRow key={`tablaDetallePlan_${index}`}>
                <TableCell>
                  {formatearFecha(
                    row.fechaCreacion,
                    "DD/MM/YYYY",
                    "DD/MM/YYYY",
                  )}
                </TableCell>
                <TableCell>
                  {formatearFecha(row.fechaPago, "DD/MM/YYYY", "DD/MM/YYYY")}
                </TableCell>
                <TableCell>{formatAsCLP(row.valorPesos)}</TableCell>
                <TableCell>{row.metodoPago}</TableCell>
                {mostrarDatosCheque && (
                  <>
                    <TableCell>{row?.banco || "-"}</TableCell>
                    <TableCell>{row?.numeroCheque || "-"}</TableCell>
                  </>
                )}
                {rolObtenido !== "CLIENTE" && (
                  <TableCell>{row.empleadoRecibePago}</TableCell>
                )}

                {/* Celda de Editar */}
                {tienePermisosEliminar() && rolObtenido !== "CLIENTE" && (
                  <TableCell align="center">
                    {puedeModificarPago(row.fechaCreacion) ? (
                      <Tooltip title="Editar pago">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleAbrirModalEditar(row)}
                          disabled={isLoadingEditPlan || isLoadingEditTarea}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="No se puede editar un pago con más de 30 días">
                        <span>
                          <IconButton size="small" disabled>
                            <EditIcon fontSize="small" color="disabled" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </TableCell>
                )}
                {/* Celda para botón eliminar */}
                {tienePermisosEliminar() && rolObtenido !== "CLIENTE" && (
                  <TableCell align="center">
                    {puedeModificarPago(row.fechaCreacion) ? (
                      <Tooltip title="Eliminar pago">
                        <IconButton
                          size="small"
                          color="secondary"
                          onClick={() => handleAbrirModalEliminar(row)}
                          disabled={isLoadingPlan || isLoadingTarea}
                        >
                          <DeleteIcon fontSize="small" color="error" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="No se puede eliminar un pago con más de 30 días">
                        <span>
                          <IconButton size="small" disabled>
                            <DeleteIcon fontSize="small" color="disabled" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="md"
      onClose={handleClose}
      aria-labelledby="factura-dialog-title"
    >
      <DialogTitle id="factura-dialog-title">
        Factura N° {factura?.folio || ""}
      </DialogTitle>
      <DialogContent>
        <DialogTitle>Detalle Factura</DialogTitle>
        <Grid container>{renderContent()}</Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          Cerrar
        </Button>
      </DialogActions>

      {/* Modal de eliminación */}
      <ModalEliminarPago
        open={modalEliminarOpen}
        onClose={handleCerrarModalEliminar}
        pago={pagoSeleccionado}
        factura={factura}
        onEliminar={handleEliminarPago}
        isLoading={isLoadingPlan || isLoadingTarea}
      />

      {/* Modal de edición */}
      <ModalEditarPago
        open={modalEditarOpen}
        onClose={handleCerrarModalEditar}
        pago={pagoAEditar}
        factura={factura}
        onEditar={handleEditarPago}
        isLoading={isLoadingEditPlan || isLoadingEditTarea}
      />
    </Dialog>
  );
};

ModalVerFactura.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  factura: PropTypes.object,
  onRefetch: PropTypes.func,
};

export default ModalVerFactura;
